import { GameContext } from './context';
import { Game } from './game';
import { GameConfig } from './game-config';
import { CarousselData } from './games-data/caroussel';
import { DragAndDropData } from './games-data/drag-and-drop';
import { JumpFormData } from './games-data/jump-form';
import { PointARelierData } from './games-data/point-a-relier';
import { PuzzleData } from './games-data/puzzle';
import { QuiVeutGagnerData } from './games-data/qui-veut-gagner';
import { ShootThemUpData } from './games-data/shoot-them-up';
import { TextATrousData } from './games-data/text-a-trous';

import { VraiFauxData } from './games-data/vrai-faux';
import {
  VideoChapter,
  VideoDownloadOption,
  VideoInteractionType,
  VideoJumpOption,
  VideoListItem,
  VideoQuestion,
} from './games-data/video-interactive';
import { ProductError, ProductErrorType, TextEntry } from '../models';
import { VideoNodalData } from './games-data/video-nodal';
import { DeffinumStoryData } from './games-data/deffinum-story';
import { DeffinumSayneteData } from './games-data/deffinum-saynete';
import { StoryData } from './games-data/story';

/**
 * ## Fichier de configuration d'une instance d'un jeu
 */
export class GameData<T = unknown> {
  /**
   * Configuration commune à tous les jeux
   */
  context: GameContext;
  /**
   * Configuration commune à tous les jeux
   */
  config: GameConfig;
  /**
   * Un objet spécifique à chacun des jeux
   *
   * Ex: [[JumpFormData]]
   */
  game: T;
  /**
   * Permet de surcharger des textes du jeu
   */
  defaultTexts: TextEntry[];
  /**
   * Version de JSON, utile pour applique une migration
   */
  version: string;
  demo?: boolean;

  constructor(rawData?: GameData<T>) {
    this.context = new GameContext(rawData?.context);
    this.config = new GameConfig(rawData?.config);
    this.game = (this.config?.gameType &&
      cloneGame(this.config.gameType, rawData.game as unknown as AnyGameData)) as unknown as T;
    this.defaultTexts = (rawData?.defaultTexts || []).map(t => new TextEntry(t));
    this.version = rawData?.version;
    this.demo = rawData?.demo || null;
  }

  static validate(gameData: GameData, parentPath: string, payload: unknown): ProductError[] {
    return [
      ...GameContext.validate(gameData.context, parentPath + '.context', payload),
      ...GameConfig.validate(gameData.config, parentPath + '.config', payload),
      ...validateGame(gameData).map(e => ({ ...e, property: `${parentPath}.game.${e.property}`, payload })),
    ];
  }
}

function validateGame(gameData: GameData): ProductError[] {
  switch (gameData.config.gameType) {
    case Game.DRAG_AND_DROP:
      return DragAndDropData.validate(gameData.game as DragAndDropData);
    case Game.PUZZLE:
      return PuzzleData.validate(gameData.game as PuzzleData);
    case Game.CAROUSSEL:
      return CarousselData.validate(gameData.game as CarousselData);
    case Game.VRAI_FAUX:
      return VraiFauxData.validate(gameData.game as VraiFauxData);
    case Game.QUI_VEUT_GAGNER:
      return QuiVeutGagnerData.validate(gameData.game as QuiVeutGagnerData);
    case Game.JUMP_FORM:
      return JumpFormData.validate(gameData.game as JumpFormData);
    case Game.TEXT_A_TROUS:
      return TextATrousData.validate(gameData.game as TextATrousData);
    case Game.POINT_A_RELIER:
      return PointARelierData.validate(gameData.game as PointARelierData);
    case Game.VIDEO_INTERACTIVE:
      return VideoInteractiveData.validate(gameData.game as VideoInteractiveData);
    case Game.VIDEO_NODAL:
      return VideoNodalData.validate();
    default:
      console.error(`No validators for ${gameData.config.gameType} be careful`);
      return [];
  }
}

export type AnyGameData =
  | CarousselData
  | DragAndDropData
  | JumpFormData
  | PointARelierData
  | PuzzleData
  | QuiVeutGagnerData
  | ShootThemUpData
  | TextATrousData
  | VideoInteractiveData
  | VideoNodalData
  | VraiFauxData
  | DeffinumStoryData
  | DeffinumSayneteData
  | StoryData;

export function cloneGame(type: Game, gameData: AnyGameData): AnyGameData {
  switch (type) {
    case Game.CAROUSSEL:
      return new CarousselData(gameData as CarousselData);
    case Game.DRAG_AND_DROP:
      return new DragAndDropData(gameData as DragAndDropData);
    // case Game.FROM_THE_SKY:
    //   return new FromTheSkyData(gameData);
    // case Game.ICE_BREAKER:
    //   return new
    case Game.JUMP_FORM:
      return new JumpFormData(gameData as JumpFormData);
    // case Game.PICK_AND_ORDER:
    //   return ???
    case Game.POINT_A_RELIER:
      return new PointARelierData(gameData as PointARelierData);
    case Game.PUZZLE:
      return new PuzzleData(gameData as PuzzleData);
    case Game.QUI_VEUT_GAGNER:
      return new QuiVeutGagnerData(gameData as QuiVeutGagnerData);
    case Game.SHOOT_THEM_UP:
      return new ShootThemUpData(gameData as ShootThemUpData);
    case Game.STORY:
      return new StoryData(gameData as StoryData);
    case Game.TEXT_A_TROUS:
      return new TextATrousData(gameData as TextATrousData);
    case Game.VIDEO_INTERACTIVE:
      return new VideoInteractiveData(gameData as VideoInteractiveData);
    case Game.VIDEO_NODAL:
      return new VideoNodalData(gameData as VideoNodalData);
    case Game.VRAI_FAUX:
      return new VraiFauxData(gameData as VraiFauxData);
    case Game.DEFFINUM_STORY:
      return new DeffinumStoryData(gameData as DeffinumStoryData);
    case Game.DEFFINUM_SAYNETE:
      return new DeffinumSayneteData(gameData as DeffinumSayneteData);
    default:
      throw new Error(type + ' is not clonable, please update libs/shared-interfaces/src/lib/data.ts');
  }
}

/**
 /$$    /$$ /$$       /$$                           /$$$$$$             /$$                                              /$$     /$$
 | $$   | $$|__/      | $$                          |_  $$_/            | $$                                             | $$    |__/
 | $$   | $$ /$$  /$$$$$$$  /$$$$$$   /$$$$$$         | $$   /$$$$$$$  /$$$$$$    /$$$$$$   /$$$$$$  /$$$$$$   /$$$$$$$ /$$$$$$   /$$ /$$    /$$ /$$$$$$
 |  $$ / $$/| $$ /$$__  $$ /$$__  $$ /$$__  $$ /$$$$$$| $$  | $$__  $$|_  $$_/   /$$__  $$ /$$__  $$|____  $$ /$$_____/|_  $$_/  | $$|  $$  /$$//$$__  $$
 \  $$ $$/ | $$| $$  | $$| $$$$$$$$| $$  \ $$|______/| $$  | $$  \ $$  | $$    | $$$$$$$$| $$  \__/ /$$$$$$$| $$        | $$    | $$ \  $$/$$/| $$$$$$$$
 \  $$$/  | $$| $$  | $$| $$_____/| $$  | $$        | $$  | $$  | $$  | $$ /$$| $$_____/| $$      /$$__  $$| $$        | $$ /$$| $$  \  $$$/ | $$_____/
 \  $/   | $$|  $$$$$$$|  $$$$$$$|  $$$$$$/       /$$$$$$| $$  | $$  |  $$$$/|  $$$$$$$| $$     |  $$$$$$$|  $$$$$$$  |  $$$$/| $$   \  $/  |  $$$$$$$
 \_/    |__/ \_______/ \_______/ \______/       |______/|__/  |__/   \___/   \_______/|__/      \_______/ \_______/   \___/  |__/    \_/    \_______/

 As any video interactive might contain a game to avoid circular dependencies
 Critical interface are moved here
 */

export class VideoInteractiveData {
  canJumpBetweenInteraction: boolean;
  video: string; // path to the video
  sendScoreAt: string;
  chapters: VideoChapter[];
  interactions: VideoInteraction[];

  constructor(rawData?: VideoInteractiveData) {
    this.canJumpBetweenInteraction = rawData?.canJumpBetweenInteraction ?? true;
    this.video = rawData?.video;
    this.sendScoreAt = rawData?.sendScoreAt;
    this.chapters = (rawData?.chapters || []).map(c => new VideoChapter(c));
    this.interactions = (rawData?.interactions || []).map(i => new VideoInteraction(i));
  }

  static validate(gameData: VideoInteractiveData, parentPath = ''): ProductError[] {
    const errors: ProductError[] = ProductError.validatePrimitiveTypes(
      gameData,
      ['video', 'sendScoreAt'],
      null,
      parentPath,
    );
    (gameData.chapters || [])
      .map((c, chapterIndex) => VideoChapter.validate(c, parentPath, { chapterIndex }))
      .reduce((flatArr, arr) => flatArr.concat(arr), [])
      .forEach(error => errors.push(error));
    (gameData.interactions || [])
      .map((i, interactionIndex) => VideoInteraction.validate(i, parentPath, { interactionIndex }))
      .reduce((flatArr, arr) => flatArr.concat(arr), [])
      .forEach(error => errors.push(error));

    return errors;
  }
}

export class VideoInteraction {
  startAt: string; // time code with the format "{min}:{sec}.[ms}", example: "0:01:15.000"
  stopAt: string; // time code with the format "{min}:{sec}.[ms}", example: "0:01:15.000"
  loopFor?: string; // duration of the loop, if there is one (time code with the format "{min}:{sec}.[ms}", example: "0:01:15.000")
  weight?: number; // is irrelevant for some games like quiz
  type: VideoInteractionType;
  title?: string; // mainly used as list question label
  questions?: VideoQuestion[];
  list?: VideoListItem[];
  game?: VideoGameItem;
  downloadOptions?: VideoDownloadOption;
  jumpOptions?: VideoJumpOption;

  constructor(rawData?: VideoInteraction) {
    this.startAt = rawData?.startAt;
    this.stopAt = rawData?.stopAt;
    this.loopFor = rawData?.loopFor;
    this.weight = rawData?.weight ?? 1;
    this.type = rawData?.type;
    this.title = rawData?.title;
    this.questions = rawData?.questions?.map(q => new VideoQuestion(q));
    this.list = rawData?.list?.map(li => new VideoListItem(li));
    this.game = rawData?.game && new VideoGameItem(rawData.game);
    this.downloadOptions = rawData?.downloadOptions && new VideoDownloadOption(rawData.downloadOptions);
    this.jumpOptions = rawData?.jumpOptions && new VideoJumpOption(rawData.jumpOptions);
  }

  static validate(
    videoInteraction: VideoInteraction,
    parentPath: string,
    payload: Record<string, unknown>,
  ): ProductError[] {
    const required = ['startAt', 'stopAt', 'type'];
    const errors: ProductError[] = ProductError.validatePrimitiveTypes(videoInteraction, required, null, parentPath) //
      .map(error => ({ ...error, payload }));

    switch (videoInteraction.type) {
      case VideoInteractionType.QUIZ:
        VideoInteraction.validateQuiz(videoInteraction.questions, parentPath, payload, errors);
        break;
      case VideoInteractionType.LIST:
        VideoInteraction.validateList(videoInteraction.list, parentPath, payload, errors);
        break;
      case VideoInteractionType.GAME:
        VideoInteraction.validateGame(videoInteraction.game, parentPath, payload, errors);
        break;
      case VideoInteractionType.DOWNLOAD:
        VideoInteraction.validateDownload(videoInteraction.downloadOptions, parentPath, payload, errors);
        break;
      case VideoInteractionType.JUMP:
        VideoInteraction.validateJump(videoInteraction.jumpOptions, parentPath, payload, errors);
        break;
    }
    return errors;
  }

  static validateQuiz(
    questions: VideoQuestion[],
    parentPath: string,
    payload: Record<string, unknown>,
    errors: ProductError[],
  ): void {
    if (questions?.length > 0) {
      questions
        .map((q, i) => VideoQuestion.validate(q, `${parentPath}questions[${i}].`, { ...payload, questionIndex: i }))
        .reduce((flatArr, arr) => flatArr.concat(arr), [])
        .forEach(error => errors.push(error));
    } else {
      errors.push({ type: ProductErrorType.MISSING, property: `${parentPath}questions`, payload });
    }
  }

  static validateList(
    list: VideoListItem[],
    parentPath: string,
    payload: Record<string, unknown>,
    errors: ProductError[],
  ): void {
    if (list?.length > 0) {
      list
        .map((listItem, i) =>
          VideoListItem.validate(listItem, `${parentPath}list[${i}].`, { ...payload, listItemIndex: i }),
        )
        .reduce((flatArr, arr) => flatArr.concat(arr), [])
        .forEach(error => errors.push(error));
    } else {
      errors.push({ type: ProductErrorType.MISSING, property: `${parentPath}list`, payload });
    }
  }

  static validateGame(
    game: VideoGameItem,
    parentPath: string,
    payload: Record<string, unknown>,
    errors: ProductError[],
  ): void {
    if (!game) {
      errors.push({ type: ProductErrorType.MISSING, property: `${parentPath}game`, payload });
    } else {
      VideoGameItem.validate(game, `${parentPath}game.`, payload) //
        .forEach(error => errors.push(error));
    }
  }

  static validateDownload(
    downloadOptions: VideoDownloadOption,
    parentPath: string,
    payload: Record<string, unknown>,
    errors: ProductError[],
  ): void {
    if (!downloadOptions) {
      errors.push({ type: ProductErrorType.MISSING, property: `${parentPath}downloadOptions`, payload });
    } else {
      VideoDownloadOption.validate(downloadOptions, `${parentPath}downloadOptions.`, payload) //
        .forEach(error => errors.push(error));
    }
  }

  static validateJump(
    options: VideoJumpOption,
    parentPath: string,
    payload: Record<string, unknown>,
    errors: ProductError[],
  ): void {
    if (!options) {
      errors.push({ type: ProductErrorType.MISSING, property: `${parentPath}jumpOptions`, payload });
    } else {
      VideoJumpOption.validate(options, `${parentPath}jumpOptions.`, payload) //
        .forEach(error => errors.push(error));
    }
  }
}

export class VideoGameItem {
  params: GameData;
  weight?: number;
  name?: string;

  constructor(rawData?: VideoGameItem) {
    this.weight = rawData?.weight ?? 1;
    this.params = new GameData({
      ...rawData.params,
      context: {
        ...rawData.params.context,
        isInVideoInteractive: true,
      },
    });
    this.name = rawData?.name;
  }

  static validate(game: VideoGameItem, parentPath: string, payload: unknown): ProductError[] {
    if (!game?.params) {
      return [{ type: ProductErrorType.MISSING, property: 'params' }];
    }
    return GameData.validate(game.params, parentPath + 'params.', payload);
  }
}
